import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, take, takeUntil } from 'rxjs/operators';
import cloneDeep from 'lodash/cloneDeep';
import { Subject } from 'rxjs';
import { FormsModule, NgForm } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { read, utils } from 'xlsx';
import { SpendDistributionService } from '../../../../../services/spend-distribution.service';
import { viewProjectSelectors } from '../../../../../store/view-project/view-project.selectors';
import { exportOneTemplate } from '../../../settings/templates/templates-export-import-utilities';
import { ProjectDto } from '../../projects.interface';
import { getSpreadSheetData } from '../spend/spend.converter';
import {
  DISTRIBUTION_TYPES,
  SPEND_ACTIONS,
  SPEND_DISTRIBUTION_STATE,
  SPEND_TYPES,
} from '../../../../../framework/constants/spend.constants';
import {
  ICommittedItemExtended,
  ILineItemExtended,
  ISubitem,
  SpendExportRow,
  SpreadSheetInputData,
} from '../../../../../store/spend/spend.interfaces';
import {
  getIsLoading,
  getSelectableYears,
  getSelectedSpendType,
  getSelectedYear,
  selectLineItemsExtended,
  selectSpendShowZeroLines,
} from '../../../../../store/spend/spend.selectors';
import { AppState } from '../../../../../store/app-state';
import { SpendViewProjectStateService } from '../../../../../services/spend-view-project-state.service';
import { saveToBackend, spendActionTypes } from '../../../../../store/spend/spend.actions';
import { SimpleButtonComponent } from '../../../../../framework/buttons/simple-medium-button/simple-button.component';
import { viewProjectActions } from '../../../../../store/view-project/view-project.actions';
import { PROJECT_VIEWS } from '../../../../../framework/constants/view-project.constants';
import { BudgetLineItemTableComponent } from './budget-line-item-table/budget-line-item-table.component';
import { SpendDistributionHeaderComponent } from './spend-distribution-header/spend-distribution-header.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { SplashScreenComponent } from 'src/app/framework/splash-screen/splash-screen.component';
import { NotificationV2Service } from '@app/notification-v2.service';
import { CurrentUserService } from '@app/services/current-user.service';
import { CheckboxComponent } from '@app/framework/inputs/checkbox/checkbox.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-spend-distribution',
  templateUrl: './spend-distribution.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./spend-distribution.component.scss'],
  providers: [NgForm],
  imports: [
    AsyncPipe,
    SpendDistributionHeaderComponent,
    BudgetLineItemTableComponent,
    SplashScreenComponent,
    SimpleButtonComponent,
    CheckboxComponent,
    FormsModule,
  ],
})
export class SpendDistributionComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly notif2 = inject(NotificationV2Service);
  private readonly state = inject(SpendViewProjectStateService);
  private readonly store = inject(Store<AppState>);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly spendDistributionService = inject(SpendDistributionService);
  private readonly userService = inject(CurrentUserService);

  syncedLineItems: ILineItemExtended[] = [];
  spreadsheetData: SpreadSheetInputData;
  SPEND_TYPES = SPEND_TYPES;
  SPEND_DISTRIBUTION_STATE = SPEND_DISTRIBUTION_STATE;
  selectedSpendType: SPEND_TYPES = SPEND_TYPES.BUDGET;
  selectedYear = 0;
  project: ProjectDto;

  selectedBudgetType$ = this.store.select(getSelectedSpendType);
  possibleYears$ = this.store.select(getSelectableYears);
  isLoading$ = this.store.select(getIsLoading);
  selectedYear$ = this.store.select(getSelectedYear);
  spendState$ = this.state.currentState;
  currentProject$ = this.store.select(viewProjectSelectors.getSelectedProject);
  isBudgetMissing$ = this.store.select(viewProjectSelectors.spendAllocationBudgetMissing);

  loadingHash: undefined | string;
  HASH_GROUP = 'spend';

  showZeroLines = toSignal(this.store.select(selectSpendShowZeroLines));
  protected readonly Number = Number;
  isDestroyed$ = new Subject<void>();

  ngOnInit() {
    this.store
      .select(selectLineItemsExtended)
      .pipe(
        takeUntil(this.isDestroyed$),
        filter((value) => !!value),
      )
      .subscribe((lineItems) => {
        this.syncedLineItems = cloneDeep(lineItems);
      });

    this.isLoading$.pipe(takeUntil(this.isDestroyed$)).subscribe((isLoading) => {
      if (isLoading) {
        this.loadingHash = this.notif2.showLoading('Loading...', { hashGroup: this.HASH_GROUP });
      } else {
        this.notif2.close(this.loadingHash);
      }
    });

    this.selectedBudgetType$.pipe(takeUntil(this.isDestroyed$)).subscribe((type) => {
      this.selectedSpendType = type;
    });

    this.selectedYear$.pipe(takeUntil(this.isDestroyed$)).subscribe((year) => {
      this.selectedYear = year;
    });

    this.currentProject$.pipe(takeUntil(this.isDestroyed$)).subscribe((project) => {
      this.project = project;
    });
  }

  ngAfterViewInit() {
    this.state.currentState.next(SPEND_DISTRIBUTION_STATE.DEFAULT);
  }

  /**
   * @deprecated, kept if needed in the future.
   * @param _
   */
  onOpenSpreadSheet = (_) => {
    this.possibleYears$.pipe(take(1)).subscribe((years) => {
      const isEditable = [];
      this.syncedLineItems.forEach((item) => {
        const isEditableItem = {};
        item.budget.forEach((budget) => {
          // warning: getDisabledMonths got refactored, it now uses only one year parameter
          // to use it again, see if this usage needs to be updated
          isEditableItem[budget.year] = this.spendDistributionService.getDisabledMonths(
            budget.year,
            item,
            'monthly_budget',
          );
        });
        isEditable.push(isEditableItem);
      });

      const items = [...getSpreadSheetData(this.syncedLineItems, years, this.selectedSpendType)];
      this.spreadsheetData = {
        items,
        isEditable,
        selectedYear: this.selectedYear,
      };
      this.changeDetectorRef.detectChanges(); // otherwise spreadsheet won't open
    });
  };

  exportBudget() {
    const sheetData: SpendExportRow[] = [];
    for (const parentItem of this.syncedLineItems) {
      sheetData.push({
        id: parentItem.id,
        parent_id: null,
        division: parentItem.division,
        cost_type: parentItem.cost_type,
        description: parentItem.name,
        dollar_amount: parentItem.project_total,
      });
      for (const subitem of parentItem.subitems) {
        sheetData.push({
          id: subitem.id,
          parent_id: parentItem.id,
          description: subitem.name,
          division: subitem.division,
          cost_type: subitem.cost_type,
          dollar_amount: subitem.project_total,
        });
      }
    }

    exportOneTemplate(sheetData, 'budget', `${this.project?.title ?? 'Project'}_budget.xlsx`);
  }

  async importBudget(file: File) {
    if (!file) {
      this.notif2.showError("Error: couldn't read the file.");
    }

    const buffer = await file.arrayBuffer();
    const workbook = read(buffer);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const parsedData = utils.sheet_to_json<SpendExportRow>(sheet);

    let parentOrder = 0;
    let subitemOrder = 0;
    // transform parsed data to line items
    let lineItems: Partial<ISubitem>[] = parsedData.map((item) => {
      const isParent = !item.parent_id;
      if (isParent) {
        parentOrder++;
        subitemOrder = 1;
      } else {
        subitemOrder++;
      }

      return {
        id: item.id,
        name: item.description,
        project_id: this.project.id,
        // parent items have manual distribution, subitems have bell curve
        distribution: item.parent_id ? DISTRIBUTION_TYPES.BELL_CURVE : DISTRIBUTION_TYPES.MANUAL,
        duration: 1,
        start_date: this.project.start_date,
        budget: [],
        original_budget_total: item.dollar_amount,
        row_number: isParent ? parentOrder : subitemOrder,
        subitems: [],
        parent_id: item.parent_id,
      } as Partial<ISubitem>;
    });

    // update parent items with total of subitems
    lineItems = lineItems.map((item) => {
      if ((item as ISubitem).parent_id) {
        return item;
      }
      return {
        ...item,
        original_budget_total: lineItems
          .filter((subItem: ISubitem) => subItem.parent_id === item.id)
          .reduce((acc, subitem) => acc + subitem.original_budget_total, 0),
      };
    });

    this.store.dispatch(spendActionTypes.budgetImported({ lineItems, project: this.project }));
  }

  /**
   * Check if the project total is equal to the initial line item total for manual distributions
   * only if the commitment start date is set
   */
  hasManualDistributionTotalMismatch() {
    if (!this.syncedLineItems?.length) {
      return false;
    }

    const manualBudgetItems = this.syncedLineItems.filter(
      (item) => item.distribution === DISTRIBUTION_TYPES.MANUAL && !!item.commitment_start_date,
    );

    let manualBudgetSubitems = [];
    if (this.userService.isPremiumSP) {
      manualBudgetSubitems = this.syncedLineItems
        .flatMap((item) => item.subitems)
        .filter(
          (subitem) =>
            subitem.distribution === DISTRIBUTION_TYPES.MANUAL && !!subitem.commitment_start_date,
        );
    }

    const manualCommittedItems = this.syncedLineItems
      .flatMap((item) => item.committed_items)
      .filter((item) => item.distribution === DISTRIBUTION_TYPES.MANUAL);

    return (
      manualBudgetItems.some((item) => item.project_total !== item.original_budget_total) ||
      manualBudgetSubitems.some(
        (subitem) => subitem.project_total !== subitem.original_budget_total,
      ) ||
      manualCommittedItems.some(
        (item: ICommittedItemExtended) => item.project_total !== item.original_budget_total,
      )
    );
  }

  closeClicked() {
    // SHOW_SPREADSHEET currently not used
    // if (this.state.currentState.value !== SPEND_DISTRIBUTION_STATE.SHOW_SPREADSHEET) {
    this.store.dispatch(viewProjectActions.viewChanged({ view: PROJECT_VIEWS.DEFAULT }));
    // }
    this.state.close(); // maybe the state isn't needed
  }

  saveClicked() {
    if (this.hasManualDistributionTotalMismatch()) {
      this.notif2.showError(
        'For manual distributions, the item total must match the initial item total.\n Hover on the item total with a red background for more information.',
        { time: 6000 },
      );
      return;
    }
    this.state.stateEvents.next(SPEND_ACTIONS.REFRESH);
    this.store.dispatch(saveToBackend());
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
    this.notif2.closeHashGroup(this.HASH_GROUP);
  }

  toggleHideZeroLines(showZeroLines: boolean) {
    this.store.dispatch(spendActionTypes.setShowZeroLines({ showZeroLines }));
  }
}
